import { apiRequest } from "../hooks/apiRequest";
import { request } from "./request";
import { toast } from "./helpers";

// Catálogo hardcodeado histórico. Fuente de arranque para `catalogoUnidades` y,
// hasta la Fase 0.5, único origen de las unidades sembradas en `store-config`.
export const unidadesHardcodeadas = [
	{ value: "UND", label: "UND" },
	{ value: "PAQUETE", label: "PAQUETE" },
	{ value: "JUEGO", label: "JUEGO" },
	{ value: "PIES", label: "PIES" },
	{ value: "PULG", label: "PULG" },
	{ value: "MTS", label: "MTS" },
	{ value: "CM", label: "CM" },
	{ value: "GLS", label: "GLS" },
	{ value: "LATA", label: "LATA" },
	{ value: "CUBETA", label: "CUBETA" },
	{ value: "CUARTOS", label: "CUARTOS" },
	{ value: "LITROS", label: "LITROS" },
	{ value: "LBS", label: "LBS" },
	{ value: "KGS", label: "KGS" },
];

const dedupOpciones = (opciones) => {
	const vistos = new Set();
	const resultado = [];

	for (const opcion of opciones) {
		const valor = String(opcion?.value ?? "").trim();
		if (!valor) continue;

		const clave = valor.toUpperCase();
		if (vistos.has(clave)) continue;

		vistos.add(clave);
		resultado.push({ ...opcion, value: valor, label: opcion.label ?? valor });
	}

	return resultado;
};

/**
 * Única fuente del catálogo de unidades para los selects. `get-unidades` ya
 * lee de la tabla `unidades` (Fase 0.5) — el histórico hardcodeado sólo se usa
 * como fallback si el endpoint falla o todavía no devuelve nada, para que los
 * selects nunca queden vacíos. `extras` es lo que el llamador ya tenga en
 * memoria (ej. una unidad recién creada desde el select).
 */
export const catalogoUnidades = async (extras = []) => {
	try {
		const resultado = await request("get-unidades");
		if (
			resultado?.success &&
			Array.isArray(resultado.data) &&
			resultado.data.length > 0
		) {
			return dedupOpciones([...resultado.data, ...extras]);
		}
	} catch (error) {
		// Se ignora: cae al catálogo hardcodeado de más abajo.
	}

	return dedupOpciones([...unidadesHardcodeadas, ...extras]);
};

/**
 * El "crear unidad al vuelo" desde el `CreatableSelect` de Productos/Items.
 * Antes posteaba a `store-config`; desde la Fase 0.5 crea en el catálogo real
 * (`POST unidades`), con `codigo` y `nombre` iguales al valor tecleado —
 * metadata como `tipo`/`codigo_dgii` se completa luego desde
 * Configuración > Unidades.
 */
export const crearUnidadConfig = async (valor) => {
	const formData = new FormData();
	formData.append("codigo", valor.toUpperCase());
	formData.append("nombre", valor.toUpperCase());
	formData.append("activa", "1");

	const respuesta = await apiRequest(formData, "unidades", "post");

	if (respuesta?.success) {
		return {
			value: respuesta.data.codigo,
			label: respuesta.data.nombre,
			id: respuesta.data.id,
			unidad_id: respuesta.data.id,
		};
	}

	if (respuesta?.code === 422) {
		const mensajes = Object.values(respuesta?.data?.message || {}).flat();
		toast(mensajes[0] || "No se pudo crear la unidad.", "error");
	} else if (respuesta) {
		toast(respuesta?.data?.message || "No se pudo crear la unidad.", "error");
	}
	// Si `respuesta` es undefined, apiRequest ya mostró su propio toast de error de red.

	return null;
};

/**
 * Variante de `catalogoUnidades` que sí trae el `id` del catálogo, para los
 * selects de Productos que necesitan guardar `unidad_id` /
 * `unidad_empaque_id`. `get-unidades` no lo expone a propósito (retrocompat
 * con los otros 5 selects que sólo esperan `{value, label}`), así que esta lee
 * directo de `GET unidades` (el CRUD de la Fase 0.5).
 */
export const catalogoUnidadesCompleto = async (extras = []) => {
	try {
		const resultado = await request("unidades?limit=200");
		if (Array.isArray(resultado?.data)) {
			const activas = resultado.data
				.filter((u) => u.activa)
				.map((u) => ({
					value: u.codigo,
					label: u.nombre,
					id: u.id,
					unidad_id: u.id,
				}));
			return dedupOpciones([...activas, ...extras]);
		}
	} catch (error) {
		// Se ignora: cae al catálogo hardcodeado, sin id (mejor un select
		// usable sin unidad_id que uno vacío).
	}

	return dedupOpciones([...unidadesHardcodeadas, ...extras]);
};

const filaFallback = (producto) => ({
	id: null,
	unidad_id: null,
	unidad: producto?.unidad || "UND",
	factor: 1,
	es_base: true,
	orden: 0,
});

/**
 * Las presentaciones del producto: la unidad (base, factor 1) y el empaque si
 * lo tiene. El backend las manda armadas en `producto.unidades`; siempre
 * devuelve ≥1 fila con exactamente una base, con fallback al string suelto
 * `producto.unidad`.
 */
export const normalizarUnidades = (producto) => {
	let lista = producto?.unidades;

	if (!Array.isArray(lista)) {
		lista = lista && typeof lista === "object" ? Object.values(lista) : [];
	}

	if (lista.length === 0) {
		return [filaFallback(producto)];
	}

	const vistos = new Set();
	const filas = [];

	for (const fila of lista) {
		const nombre = String(fila?.unidad ?? "").trim();
		if (!nombre) continue;

		const clave = nombre.toUpperCase();
		if (vistos.has(clave)) continue;
		vistos.add(clave);

		filas.push({
			id: fila.id ?? null,
			unidad_id: fila.unidad_id ?? null,
			unidad: nombre,
			factor: Number(fila.factor) || 1,
			es_base: Boolean(fila.es_base),
			orden: fila.orden ?? 0,
		});
	}

	if (filas.length === 0) {
		return [filaFallback(producto)];
	}

	if (!filas.some((fila) => fila.es_base)) {
		let indiceBase = 0;
		for (let i = 1; i < filas.length; i++) {
			if (filas[i].factor < filas[indiceBase].factor) indiceBase = i;
		}
		filas[indiceBase].es_base = true;
	}

	return filas.sort((a, b) => a.factor - b.factor);
};

export const unidadBase = (producto) => {
	const filas = normalizarUnidades(producto);
	return filas.find((fila) => fila.es_base) ?? filas[0];
};

/** `[{value, label, factor, es_base, id, unidad_id}]` para react-select. */
export const opcionesUnidades = (producto) => {
	const filas = normalizarUnidades(producto);

	return filas.map((fila) => ({
		value: fila.unidad,
		label:
			fila.es_base || filas.length === 1
				? fila.unidad
				: `${fila.unidad} (x${fila.factor})`,
		factor: fila.factor,
		es_base: fila.es_base,
		id: fila.id,
		unidad_id: fila.unidad_id,
	}));
};

/**
 * ¿La línea cobra el precio de paquete propio de la lista de precio? Sólo en
 * el empaque, y sólo si la lista lo tiene (null = calculado).
 */
export const usaPrecioPaquete = (fila, precioPaquete) =>
	Number(fila?.factor) > 1 && precioPaquete != null && precioPaquete !== "";

/**
 * El precio de una lista en la unidad de la línea: el precio de paquete propio
 * de la lista si la unidad es el empaque y lo tiene, si no factor × precio.
 */
export const precioPorUnidad = (precioBase, fila, precioPaquete = null) => {
	if (usaPrecioPaquete(fila, precioPaquete)) return Number(precioPaquete);
	return Number(precioBase || 0) * Number(fila?.factor ?? 1);
};

export const aCantidadBase = (cantidad, factor) => {
	const c = Number(cantidad);
	const f = Number(factor);
	if (!Number.isFinite(c) || !Number.isFinite(f) || f === 0) return 0;
	return c * f;
};

export const aCantidadUnidad = (cantidadBase, factor) => {
	const c = Number(cantidadBase);
	const f = Number(factor);
	if (!Number.isFinite(c) || !Number.isFinite(f) || f === 0) return 0;
	return c / f;
};

/**
 * Una existencia en unidad base expresada en empaques + unidades sueltas
 * ("2 CAJA + 5 UND"). Sin empaque, sólo las unidades.
 */
export const desglosarEnEmpaques = (cantidadBase, producto) => {
	const total = Number(cantidadBase) || 0;
	const unidad = producto?.unidad || "UND";
	const cantidadEmpaque = Number(producto?.cantidad_empaque) || 0;

	if (!producto?.unidad_empaque || cantidadEmpaque <= 1) {
		return `${total} ${unidad}`;
	}

	const empaques = Math.floor(total / cantidadEmpaque);
	const sueltas = Math.round((total - empaques * cantidadEmpaque) * 10000) / 10000;

	return `${empaques} ${producto.unidad_empaque} + ${sueltas} ${unidad}`;
};

/** Absorbe los sitios de impresión que hoy leen `producto.producto.unidad`. */
export const unidadDeLinea = (linea) => linea?.unidad ?? linea?.producto?.unidad ?? "";

const redondear = (numero) => Math.round((Number(numero) || 0) * 10000) / 10000;

/** El nombre de una fila de unidad venga del catálogo (`unidad`) o de un <Select> (`value`). */
const nombreUnidad = (unidad) => unidad?.value ?? unidad?.unidad ?? "";

/**
 * El empaque del producto: la fila de mayor factor, o null si sólo tiene la
 * unidad base. `normalizarUnidades` ya devuelve las filas ordenadas por factor.
 */
export const filaEmpaque = (producto) => {
	const filas = normalizarUnidades(producto);
	const mayor = filas[filas.length - 1];
	return mayor && mayor.factor > 1 ? mayor : null;
};

/**
 * Cuánto contiene el empaque con que se facturó una línea ("12 UND"), para
 * imprimirlo junto al nombre del producto. Vacío cuando la línea se vendió en
 * la unidad base: un factor 1 no dice nada.
 */
export const contenidoEmpaqueDeLinea = (linea) => {
	const factor = Number(linea?.factor) || 1;
	if (factor <= 1) return "";

	const base = linea?.producto?.unidad || "UND";
	return `${redondear(factor)} ${base}`;
};

/**
 * Una cantidad en unidad base dicha también en empaques ("25 UND (2 CAJA + 1
 * UND)"), para los mensajes de los checks de cantidad. El empaque es el de la
 * línea cuando ya eligió uno, si no el del producto; sin empaque queda sólo la
 * base, porque "25 UND (25 UND)" no aporta nada. Las cantidades ≤ 0 tampoco se
 * desglosan: un desglose negativo confunde más de lo que explica.
 */
export const describirCantidad = (cantidadBase, producto, unidadLinea = null) => {
	const total = redondear(cantidadBase);
	const base = unidadBase(producto)?.unidad || "UND";
	const enBase = `${total} ${base}`;

	const empaque = Number(unidadLinea?.factor) > 1 ? unidadLinea : filaEmpaque(producto);
	const factor = Number(empaque?.factor) || 1;
	const nombre = nombreUnidad(empaque);

	if (total <= 0 || factor <= 1 || !nombre) return enBase;

	const empaques = Math.floor(total / factor);
	const sueltas = redondear(total - empaques * factor);
	const desglose = sueltas > 0 ? `${empaques} ${nombre} + ${sueltas} ${base}` : `${empaques} ${nombre}`;

	return `${enBase} (${desglose})`;
};

/**
 * Agrupa líneas por la unidad base del producto sumando `cantidad_base`
 * (fallback a `cantidad` en líneas anteriores al snapshot). La clave es la
 * unidad del producto, no la de la línea: `cantidad_base` ya está en base, y
 * etiquetarla "CAJA" mostraría 48 CAJAS donde se despacharon 2.
 */
export const agruparPorUnidadBase = (lineas) => {
	return (lineas || []).reduce((acc, linea) => {
		const unidad = linea?.producto?.unidad || unidadDeLinea(linea) || "SIN UNIDAD";
		const clave = unidad.toUpperCase();
		const cantidad = Number(linea.cantidad_base ?? linea.cantidad) || 0;

		acc[clave] = (acc[clave] ?? 0) + cantidad;
		return acc;
	}, {});
};

/**
 * La fila de unidad del producto que corresponde a una línea ya guardada.
 * Del autoguardado viene `unidad_id`; del backend sólo el snapshot `unidad`
 * (nombre) y `factor`, porque las tablas de detalle no guardan la FK.
 * Devuelve null si no hay coincidencia: la línea cae en la unidad base.
 */
export const filaUnidadDeLinea = (filas, linea) => {
	if (!linea) return null;

	if (linea.unidad_id) {
		const porId = filas.find((f) => Number(f.unidad_id) === Number(linea.unidad_id));
		if (porId) return porId;
	}

	const nombre = String(linea.unidad ?? "").trim().toUpperCase();
	if (!nombre) return null;

	return filas.find((f) => f.unidad.toUpperCase() === nombre) ?? null;
};
